import React from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Plus, Pencil, Trash2, Calendar, X, Check, Bell } from 'lucide-react';

const WebinarForm = ({ webinarForm, setWebinarForm, isEditing, onSubmit, onCancel }) => (
  <Card className="bg-slate-800 border-slate-700">
    <CardHeader>
      <CardTitle className="text-white flex items-center justify-between">
        {isEditing ? 'Edit Webinar' : 'Add New Webinar'}
        <Button variant="ghost" size="sm" onClick={onCancel}>
          <X className="h-4 w-4" />
        </Button>
      </CardTitle>
    </CardHeader>
    <CardContent>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="text-sm text-slate-400 mb-1 block">Title *</label>
            <Input
              value={webinarForm.title}
              onChange={(e) => setWebinarForm({ ...webinarForm, title: e.target.value })}
              required
              className="bg-slate-900 border-slate-600"
              data-testid="webinar-title-input"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-sm text-slate-400 mb-1 block">Description *</label>
            <textarea
              value={webinarForm.description}
              onChange={(e) => setWebinarForm({ ...webinarForm, description: e.target.value })}
              required
              rows={3}
              className="w-full bg-slate-900 border border-slate-600 rounded-md p-2 text-white"
            />
          </div>
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Presenter *</label>
            <Input
              value={webinarForm.presenter}
              onChange={(e) => setWebinarForm({ ...webinarForm, presenter: e.target.value })}
              required
              className="bg-slate-900 border-slate-600"
            />
          </div>
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Presenter Title</label>
            <Input
              value={webinarForm.presenter_title}
              onChange={(e) => setWebinarForm({ ...webinarForm, presenter_title: e.target.value })}
              placeholder="e.g., CPA, Estate Attorney"
              className="bg-slate-900 border-slate-600"
            />
          </div>
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Date *</label>
            <Input
              type="date"
              value={webinarForm.date}
              onChange={(e) => setWebinarForm({ ...webinarForm, date: e.target.value })}
              required
              className="bg-slate-900 border-slate-600"
            />
          </div>
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Time *</label>
            <Input
              type="time"
              value={webinarForm.time}
              onChange={(e) => setWebinarForm({ ...webinarForm, time: e.target.value })}
              required
              className="bg-slate-900 border-slate-600"
            />
          </div>
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Duration (minutes)</label>
            <Input
              type="number"
              value={webinarForm.duration_minutes}
              onChange={(e) => setWebinarForm({ ...webinarForm, duration_minutes: e.target.value })}
              className="bg-slate-900 border-slate-600"
            />
          </div>
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Category *</label>
            <Input
              value={webinarForm.category}
              onChange={(e) => setWebinarForm({ ...webinarForm, category: e.target.value })}
              required
              placeholder="e.g., Estate Planning"
              className="bg-slate-900 border-slate-600"
            />
          </div>
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Max Attendees</label>
            <Input
              type="number"
              value={webinarForm.max_attendees}
              onChange={(e) => setWebinarForm({ ...webinarForm, max_attendees: e.target.value })}
              placeholder="Leave empty for unlimited"
              className="bg-slate-900 border-slate-600"
            />
          </div>
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Meeting Link</label>
            <Input
              value={webinarForm.meeting_link}
              onChange={(e) => setWebinarForm({ ...webinarForm, meeting_link: e.target.value })}
              placeholder="Zoom/Teams link"
              className="bg-slate-900 border-slate-600"
            />
          </div>
        </div>
        <div className="flex gap-2 pt-4">
          <Button type="submit" className="bg-amber-600 hover:bg-amber-700" data-testid="save-webinar-btn">
            <Check className="h-4 w-4 mr-2" />
            {isEditing ? 'Update' : 'Create'} Webinar
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </CardContent>
  </Card>
);

export default function WebinarsTab({
  webinars,
  pendingWebinars,
  showForm,
  webinarForm,
  setWebinarForm,
  editingItem,
  onAddNew,
  onEdit,
  onSubmit,
  onCancel,
  onDelete,
  onApprovePending,
  onRejectPending,
}) {
  if (showForm) {
    return (
      <WebinarForm
        webinarForm={webinarForm}
        setWebinarForm={setWebinarForm}
        isEditing={!!editingItem}
        onSubmit={onSubmit}
        onCancel={onCancel}
      />
    );
  }

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <Button
          onClick={onAddNew}
          className="bg-amber-600 hover:bg-amber-700"
          data-testid="add-webinar-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Webinar
        </Button>
      </div>

      {pendingWebinars.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-amber-400 mb-3 flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Pending Webinar Proposals ({pendingWebinars.length})
          </h3>
          <div className="grid gap-4">
            {pendingWebinars.map(webinar => (
              <Card key={webinar.webinar_id} className="bg-amber-900/20 border-amber-700" data-testid={`pending-webinar-${webinar.webinar_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-white">{webinar.title}</h3>
                        <Badge className="bg-amber-600">Pending Review</Badge>
                      </div>
                      <p className="text-slate-400 text-sm mb-2">{webinar.description?.substring(0, 150)}...</p>
                      <div className="flex flex-wrap gap-4 text-xs text-slate-500 mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {webinar.date} at {webinar.time}
                        </span>
                        <span>Presenter: {webinar.presenter}</span>
                        <span>Category: {webinar.category}</span>
                        <span>Duration: {webinar.duration_minutes} min</span>
                      </div>
                      <p className="text-xs text-amber-400">
                        Proposed by: {webinar.presenter_email || 'Unknown'}
                      </p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button
                        size="sm"
                        onClick={() => onApprovePending(webinar.webinar_id)}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid={`approve-webinar-${webinar.webinar_id}`}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => onRejectPending(webinar.webinar_id)}
                        data-testid={`reject-webinar-${webinar.webinar_id}`}
                      >
                        <X className="h-4 w-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      <h3 className="text-lg font-semibold text-white mb-3">All Webinars</h3>
      <div className="grid gap-4">
        {webinars.map(webinar => (
          <Card key={webinar.webinar_id} className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-white">{webinar.title}</h3>
                    <Badge className={
                      webinar.status === 'upcoming' ? 'bg-green-600' :
                        webinar.status === 'live' ? 'bg-red-600' :
                          webinar.status === 'completed' ? 'bg-slate-600' : 'bg-slate-600'
                    }>
                      {webinar.status}
                    </Badge>
                  </div>
                  <p className="text-slate-400 text-sm mb-2">{webinar.description?.substring(0, 150)}...</p>
                  <div className="flex flex-wrap gap-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {webinar.date} at {webinar.time}
                    </span>
                    <span>Presenter: {webinar.presenter}</span>
                    <span>Category: {webinar.category}</span>
                    <span>Registered: {webinar.registered_count || 0}{webinar.max_attendees ? `/${webinar.max_attendees}` : ''}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(webinar)}
                    className="text-slate-400 hover:text-white"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(webinar)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {webinars.length === 0 && (
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-8 text-center">
              <Calendar className="h-12 w-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No webinars yet. Create your first one!</p>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
}
